// B-80 — "Rehacer" one closing: start it again from its reports.
//
// Opens on `previewCierreRedo` — what will be lost: the values a person typed (and what each goes
// back to), the alerts a person attended with their observation — and lets the person choose new
// files for the office's own documents. Every file is optional: one chosen replaces the stored one,
// the rest are read again from what is stored. A report filed once for the whole day (`scope:
// client`, Bancamía's three) is not offered here — it is every office's file; it is re-read, and a
// new one goes in through "Procesar el día".
//
// "Rehacer cierre" is `redoCierreClosing`; a refusal keeps the dialog open with its reason. The
// sections are `utils/cierreRehacer.js`'s, the frame `ConfirmarRehacer`.
import { useEffect, useState } from "react";
import { FileCheck2, FileText } from "lucide-react";
import { cn } from "@mantis-sas/ui";

import { previewCierreRedo, redoCierreClosing } from "../../../ApiCalls/mantisBusiness";
import { acceptDeFormato } from "../../../utils/cierreManual";
import { nombreDeFuente } from "../../../utils/cierreNombres";
import { errorDeRehacer, seccionesDeRehacer } from "../../../utils/cierreRehacer";
import { ConfirmarRehacer } from "./ConfirmarRehacer";
import { guia } from "../../../utils/cierreTours";

// The preview, the chosen files and the send — out of the frame so the dialog only draws.
export function useRehacerCierre({ token, mantisBusinessId, closingId, abierto }) {
  const [preview, setPreview] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState("");
  const [archivos, setArchivos] = useState({});
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!abierto || !closingId) return undefined;
    let activo = true;
    setPreview(null);
    setArchivos({});
    setMotivo("");
    setError("");
    setErrorCarga("");
    setCargando(true);
    (async () => {
      const response = await previewCierreRedo(token, mantisBusinessId, closingId).catch(() => null);
      if (!activo) return;
      setCargando(false);
      if (!response?.success) {
        setErrorCarga(errorDeRehacer(response));
        return;
      }
      setPreview(response.data);
    })();
    return () => {
      activo = false;
    };
  }, [abierto, token, mantisBusinessId, closingId]);

  const elegir = (clave, file) => {
    setError("");
    setArchivos((actual) => ({ ...actual, [clave]: file || null }));
  };

  // Answers the stored result, or null when refused (the reason is in `error`).
  const rehacer = async () => {
    setError("");
    setEnviando(true);
    const response = await redoCierreClosing(token, mantisBusinessId, closingId, archivos, motivo).catch(() => null);
    setEnviando(false);
    if (!response?.success) {
      setError(errorDeRehacer(response));
      return null;
    }
    return response.data;
  };

  return {
    preview, cargando, errorCarga, archivos, elegir, motivo, setMotivo, enviando, error, rehacer,
  };
}

function Documento({ documento, nombres, archivo, deshabilitado, onElegir }) {
  const nombre = nombreDeFuente(nombres, documento.documentKey);
  const actual = documento.current?.originalFilename || documento.current?.filename || null;
  const id = `rehacer-${documento.documentKey}`;
  return (
    <li className={cn("flex items-center gap-2.5 px-3 py-2", archivo && "bg-brinks-success-soft/60")}>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-brinks-surface text-brinks-muted-foreground">
        {archivo ? <FileCheck2 className="h-4 w-4 text-brinks-success" /> : <FileText className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-brinks-foreground">{nombre}</p>
        <p className="truncate text-[11.5px] text-brinks-muted-foreground">
          {archivo
            ? `Nuevo: ${archivo.name}`
            : !documento.replaceable
              ? "Reporte del día: se vuelve a leer el que ya está cargado"
              : actual
                ? `Se vuelve a leer: ${actual}`
                : "Sin archivo cargado"}
        </p>
      </div>
      {documento.replaceable ? (
        <>
          <label
            htmlFor={id}
            className={cn(
              "inline-flex h-8 shrink-0 items-center rounded-[7px] border bg-card px-3 text-[12px] font-medium text-brinks-foreground transition",
              deshabilitado ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-brinks-border-strong"
            )}
          >
            {archivo ? "Cambiar" : "Reemplazar"}
          </label>
          <input
            id={id}
            type="file"
            aria-label={`Archivo nuevo para ${nombre}`}
            accept={acceptDeFormato(documento.format)}
            disabled={deshabilitado}
            onChange={(e) => onElegir(documento.documentKey, e.target.files?.[0] || null)}
            className="sr-only"
          />
        </>
      ) : null}
    </li>
  );
}

export function RehacerCierreDialog({ open, onOpenChange, token, mantisBusinessId, closingId, nombres, onRehecho }) {
  const r = useRehacerCierre({ token, mantisBusinessId, closingId, abierto: open });
  const preview = r.preview;

  const confirmar = async () => {
    const resultado = await r.rehacer();
    if (!resultado) return;
    onOpenChange(false);
    if (onRehecho) onRehecho(resultado);
  };

  return (
    <ConfirmarRehacer
      open={open}
      onOpenChange={onOpenChange}
      titulo="Rehacer cierre"
      descripcion="Vuelve a procesar el cierre desde sus reportes. Puedes reemplazar los archivos que quieras; los demás se toman de la carga anterior."
      cargando={r.cargando}
      errorCarga={r.errorCarga}
      aviso={[
        preview && !preview.redoable ? preview.refusal : "",
        preview?.terminal ? `El cierre está ${preview.status || "cerrado"}: se vuelve a abrir y se recalcula.` : "",
      ].filter(Boolean)}
      secciones={seccionesDeRehacer(preview, nombres, r.archivos)}
      motivo={r.motivo}
      onMotivo={r.setMotivo}
      error={r.error}
      enviando={r.enviando}
      deshabilitado={!preview || !preview.redoable}
      etiquetaConfirmar="Rehacer cierre"
      onConfirmar={confirmar}
    >
      {preview?.documents?.length ? (
        <section className="space-y-1.5">
          <h3 className="text-[12.5px] font-semibold text-brinks-foreground">Documentos</h3>
          <ul {...guia("Documentos de la oficina", "Si un documento de la oficina cambió, sube aquí el nuevo. Los que no subas se vuelven a leer tal como están guardados.")} className="divide-y overflow-hidden rounded-[10px] border bg-card">
            {preview.documents.map((documento) => (
              <Documento
                key={documento.documentKey}
                documento={documento}
                nombres={nombres}
                archivo={r.archivos[documento.documentKey]}
                deshabilitado={r.enviando || !preview.redoable}
                onElegir={r.elegir}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </ConfirmarRehacer>
  );
}
